import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "app-content" }

import { onMounted, onUnmounted, watch, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroBanner from './components/base/HeroBanner.vue';
import AppHeader from '@/components/base/AppHeader.vue'
import AppFooter from './components/base/AppFooter.vue';
import ToastComponent from '@/components/ToastComponent.vue'
import { useToast } from '@/composables/useToast'
import ThemeToggle from '@/components/ThemeToggle.vue'
import BetslipModal from '@/components/BetslipModal.vue'
import { useModalStore } from '@/stores/modal'
import { useAuthStore } from '@/stores/auth'
import { useGameStore } from '@/stores/game'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'
import { getThemeParamFromUrl, initializeTheme, setTheme } from '@/composables/useTheme'
import { resolveThemeName } from '@/utils/themeLoader'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { setApiBaseUrl } from '@/api'
import { useI18n } from 'vue-i18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props, { expose: __expose }) {

const { toast, showToast } = useToast()
const modalStore = useModalStore()
const authStore = useAuthStore()
const gameStore = useGameStore()
const upcomingDrawsStore = useUpcomingDrawsStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const iframeCleanupHandlers: Array<() => void> = []

const isInitialized = ref(false)

const openBetslipModal = () => {
  modalStore.openBetslip()
}

const closeBetslipModal = () => {
  modalStore.closeBetslip()
}

const refreshAccountScopedCatalog = async () => {
  upcomingDrawsStore.invalidateAccountContext()
  await upcomingDrawsStore.fetchDraws(1, undefined, true)
  gameStore.clearProducts()
}

watch(
  () => authStore.account,
  async (newAccount, oldAccount) => {
    if (!newAccount || newAccount === oldAccount) {
      return
    }
    try {
      await refreshAccountScopedCatalog()
    } catch (error) {
      console.error('Failed to refresh draws after account change:', error)
    }
  },
  { immediate: false }
)
// Watch for account changes in URL
watch(
  () => route.query.account,
  async (newAccount, oldAccount) => {
    if (newAccount !== oldAccount && newAccount && typeof newAccount === 'string') {
      authStore.account = newAccount
      sessionStorage.setItem('auth_account', newAccount)
      
      try {
        await initializeTheme(true)
        const currency = sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshAccountScopedCatalog()
      } catch (error) {
        console.error('Failed to re-initialize theme:', error)
      }
    }
  },
  { immediate: false }
)

// Only apply game-settings theme when URL has no theme param (URL wins)
watch(
  () => gameStore.getBetSettings?.theme,
  async (newTheme) => {
    if (newTheme && !getThemeParamFromUrl()) {
      try {
        await setTheme(resolveThemeName(newTheme))
      } catch (error) {
        console.error('Failed to update theme from game settings:', error)
      }
    }
  },
  { immediate: false }
)

const isInIframe = iframeCommunication.isRunningInIframe()
const isMinimalChrome = computed(() => route.meta.minimalChrome === true)
const showAppChrome = computed(() => !isMinimalChrome.value)

onMounted(async () => {
  const handleParentLogout = () => {
    authStore.logout({ silent: true, reason: 'host-request' })
    modalStore.closeBetslip()
    showToast({
      title: t('common.loggedOut'),
      message: t('common.sessionClosed'),
      type: 'info',
      duration: 4000
    })
    router.replace({ path: '/next-draws', query: {} })
  }

  const handleParentNavigate = (data?: Record<string, unknown>) => {
    try {
      if (!data) return
      const target = data.route || data.path || data.name
      if (!target || typeof target !== 'string') return
      
      const query = (data.query && typeof data.query === 'object') 
        ? data.query as Record<string, string> 
        : undefined

      if (target.startsWith('/')) {
        router.push({ path: target, query })
      } else {
        router.push({ name: target, query })
      }
    } catch (e) {
      console.error('Iframe navigation failed:', e)
    }
  }

  // CORE HANDSHAKE HANDLER: This is where configuration is synchronized
  const handleParentReady = async (data?: Record<string, unknown>) => {
    console.log('[App] Handshake received from parent:', data)
    if (data?.config && typeof data.config === 'object') {
      const config = data.config as Record<string, any>
      
      // 1. Synchronize Authentication/Account state (tenant account kept without token)
      authStore.applyParentConfig(config)

      // 2. Apply dynamic API base URL
      if (config.GAME_SERVER_URL) {
        setApiBaseUrl(config.GAME_SERVER_URL)
      }

      // 3. Trigger initial settings fetch with correct URL/Headers
      try {
        await initializeTheme(true)
        const currency = config.currency || sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshAccountScopedCatalog()
        if (route.meta.minimalChrome && authStore.account) {
          await gameStore.fetchProducts()
        }
      } catch (e) {
        console.warn('Initialization after handshake failed:', e)
      }
    } else {
      // No config provided by parent, just initialize theme
      await initializeTheme()
    }
    
    // 4. Reveal the app content
    isInitialized.value = true
  }

  // Register communication listeners
  iframeCleanupHandlers.push(iframeCommunication.on('logout', handleParentLogout))
  iframeCleanupHandlers.push(iframeCommunication.on('navigate', handleParentNavigate))
  iframeCleanupHandlers.push(iframeCommunication.on('parent-ready', handleParentReady))

  if (isInIframe) {
    document.body.classList.add('iframe-mode')
    document.documentElement.classList.add('iframe-mode')
    
    // Measure and report height
    iframeCommunication.startObserving('iframe-root')
    
    // START HANDSHAKE: Tell parent child is ready to receive configuration
    iframeCommunication.sendReady()

    // Safety timeout: If handshake doesn't complete in 4s, proceed with default init
    setTimeout(async () => {
      if (!isInitialized.value) {
        console.warn('[App] Handshake timeout - proceeding with default initialization')
        await initializeTheme()
        await refreshAccountScopedCatalog()
        isInitialized.value = true
      }
    }, 4000)

    // Handle deep links from URL params
    const gameSlug = (route.query.game || route.query.gameSlug) as string | undefined
    if (gameSlug && typeof gameSlug === 'string' && gameSlug.trim()) {
      const slug = gameSlug.trim()
      const restQuery = { ...route.query }
      delete restQuery.game
      delete restQuery.gameSlug
      router.replace({ path: `/lottery/${slug}`, query: restQuery as Record<string, string> })
    }
  } else {
    // Standard standalone mode
    const rawAccount =
      authStore.account ||
      sessionStorage.getItem('auth_account') ||
      (route.query.account as string | undefined)
    const account =
      typeof rawAccount === 'string'
        ? rawAccount.trim()
        : rawAccount != null
          ? String(rawAccount).trim()
          : ''
    if (account) {
      authStore.account = account
      sessionStorage.setItem('auth_account', account)
    }

    const rawToken = route.query.token as string | undefined
    const token =
      typeof rawToken === 'string' ? rawToken.trim() : rawToken != null ? String(rawToken).trim() : ''
    if (token) {
      authStore.token = token
      sessionStorage.setItem('auth_token', token)
    }

    try {
      await initializeTheme()
      const currency = sessionStorage.getItem('auth_currency')
      if (account && currency) {
        await gameStore.fetchGameSettings(false)
      }
      if (account && route.meta.minimalChrome) {
        await gameStore.fetchProducts()
      }
    } catch (e) {
      console.warn('Failed to load initial settings:', e)
    }
    
    isInitialized.value = true
  }
})

onUnmounted(() => {
  iframeCommunication.destroy()
  iframeCleanupHandlers.forEach((cleanup) => cleanup())
})

__expose({
  openBetslipModal,
  closeBetslipModal
})

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    id: "iframe-root",
    class: _normalizeClass({ 'is-iframe': _unref(isInIframe), 'minimal-chrome': isMinimalChrome.value })
  }, [
    (showAppChrome.value)
      ? (_openBlock(), _createBlock(HeroBanner, { key: 0 }))
      : _createCommentVNode("", true),
    (showAppChrome.value)
      ? (_openBlock(), _createBlock(AppHeader, { key: 1 }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_router_view, { onOpenBetslipModal: openBetslipModal }),
      (showAppChrome.value)
        ? (_openBlock(), _createBlock(AppFooter, { key: 0 }))
        : _createCommentVNode("", true),
      (showAppChrome.value)
        ? (_openBlock(), _createBlock(ThemeToggle, {
            key: 1,
            class: "desktop-only"
          }))
        : _createCommentVNode("", true),
      (_unref(toast))
        ? (_openBlock(), _createBlock(ToastComponent, {
            key: 2,
            title: _unref(toast).title,
            message: _unref(toast).message,
            type: _unref(toast).type,
            duration: _unref(toast).duration
          }, null, 8, ["title", "message", "type", "duration"]))
        : _createCommentVNode("", true),
      _createVNode(BetslipModal, {
        "is-open": _unref(modalStore).showBetslip,
        onClose: closeBetslipModal
      }, null, 8, ["is-open"])
    ])
  ], 2))
}
}

})